<template>
  <div id="proforma-pdf" class="proforma-pdf">
    <div class="header">
      <img
        v-if="empresaLogo"
        :src="'data:image/png;base64, ' + empresaLogo"
        alt="Logo Empresa"
        class="logo"
      />
      <div>
        <h1>PROFORMA</h1>
        <div class="numero">Nº {{ proforma.numero }}</div>
      </div>
    </div>
    <hr />
    <div class="info">
      <div><strong>Cliente:</strong> {{ proforma.cliente }}</div>
      <div><strong>Fecha:</strong> {{ proforma.fecha }}</div>
      <div><strong>Vendedor:</strong> {{ proforma.vendedor }}</div>
      <div><strong>Válido hasta:</strong> {{ proforma.valido_hasta }}</div>
    </div>
    <div v-if="detalleArray.length" class="detalle-section">
      <h3>Detalle de Productos</h3>
      <table class="detalle-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Código Interno</th>
            <th>Marca</th>
            <th>Producto</th>
            <th class="text-right">Cantidad</th>
            <th class="text-right">Precio</th>
            <th class="text-right">C/U</th>
            <th class="text-center"># Días Entrega</th>
            <th>Subtotal</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in detalleArray" :key="idx">
            <td>{{ idx + 1 }}</td>
            <td>
              {{
                item.codigo_interno === (item.descripcion || item.producto)
                  ? item.id
                  : item.codigo_interno
              }}
            </td>
            <td>{{ item.marca || "NO DEFINIDO" }}</td>
            <td>{{ item.producto || item.descripcion }}</td>
            <td class="text-right">
              {{ item.pivot?.cantidad ?? item.cantidad }}
            </td>
            <td class="text-right">{{ item.pivot?.precio ?? item.precio }}</td>
            <td class="text-right">
              {{ calcularPrecioUnitario(item) }}
            </td>
            <td class="text-center">
              {{ item.pivot?.nro_dias ?? item.nro_dias ?? "-" }}
            </td>
            <td>
              {{
                item.subtotal ||
                (
                  (item.pivot?.cantidad ?? item.cantidad) *
                  (item.pivot?.precio ?? item.precio)
                ).toFixed(2)
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="total">
      <div class="totales-grid-wrapper">
        <div class="totales-grid">
          <div class="tot-label">Subtotal:</div>
          <div class="tot-value">{{ subtotalGeneral }}</div>
          <div class="tot-label">Descuentos:</div>
          <div class="tot-value">{{ totalDescuentos }}</div>
          <div class="tot-label tot-main">Monto Total:</div>
          <div class="tot-value tot-main monto">{{ proforma.total }}</div>
        </div>
      </div>
      <div v-if="montoEnLetras" class="monto-letras">
        <strong>SON:</strong> {{ montoEnLetras }}
      </div>
    </div>
    <div class="obs">
      <strong>Observación:</strong> {{ proforma.observaciones }}
    </div>
  </div>
</template>

<script>
import EmpresaService from "@/service/EmpresaService";
export default {
  name: "ProformaPdf",
  props: {
    proforma: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      empresaLogo: "",
    };
  },
  computed: {
    subtotalGeneral() {
      // Suma de (precio * cantidad) de todos los items
      return this.detalleArray
        .reduce((acc, item) => {
          const cantidad = item.pivot?.cantidad ?? item.cantidad ?? 0;
          const precio = item.pivot?.precio ?? item.precio ?? 0;
          return acc + precio * cantidad;
        }, 0)
        .toFixed(2);
    },
    totalDescuentos() {
      // Suma de descuentos netos de todos los items
      return this.detalleArray
        .reduce((acc, item) => {
          const descuento =
            item.pivot?.descuento_neto ?? item.descuento_neto ?? 0;
          return acc + (parseFloat(descuento) || 0);
        }, 0)
        .toFixed(2);
    },
    detalleArray() {
      const detalles = this.proforma.detalles;
      if (Array.isArray(detalles)) {
        return detalles.map((item) => ({
          ...item,
          precio_unitario: this.calcularPrecioUnitario(item),
        }));
      }
      if (detalles && typeof detalles === "object") {
        return Object.values(detalles)
          .filter((item) => item && typeof item === "object")
          .map((item) => ({
            ...item,
            precio_unitario: this.calcularPrecioUnitario(item),
          }));
      }
      return [];
    },
    montoEnLetras() {
      return this.numeroALetras(this.proforma.total);
    },
  },
  methods: {
    calcularPrecioUnitario(item) {
      const cantidad = item.pivot?.cantidad ?? item.cantidad ?? 0;
      const precio = item.pivot?.precio ?? item.precio ?? 0;
      const descuento = item.pivot?.descuento_neto ?? item.descuento_neto ?? 0;
      if (!cantidad || !precio) return "-";
      return (precio * cantidad - descuento).toFixed(2);
    },
    // Convierte un número a letras en español (simple, hasta millones)
    numeroALetras(num) {
      if (typeof num === "string") num = parseFloat(num);
      if (isNaN(num)) return "-";
      const unidades = [
        "",
        "uno",
        "dos",
        "tres",
        "cuatro",
        "cinco",
        "seis",
        "siete",
        "ocho",
        "nueve",
      ];
      const decenas = [
        "",
        "diez",
        "veinte",
        "treinta",
        "cuarenta",
        "cincuenta",
        "sesenta",
        "setenta",
        "ochenta",
        "noventa",
      ];
      const centenas = [
        "",
        "cien",
        "doscientos",
        "trescientos",
        "cuatrocientos",
        "quinientos",
        "seiscientos",
        "setecientos",
        "ochocientos",
        "novecientos",
      ];
      const especiales = {
        10: "diez",
        11: "once",
        12: "doce",
        13: "trece",
        14: "catorce",
        15: "quince",
        20: "veinte",
        21: "veintiuno",
        22: "veintidos",
        23: "veintitres",
        24: "veinticuatro",
        25: "veinticinco",
        26: "veintiseis",
        27: "veintisiete",
        28: "veintiocho",
        29: "veintinueve",
      };
      function seccion(n) {
        if (n === 0) return "";
        if (especiales[n]) return especiales[n];
        if (n < 10) return unidades[n];
        if (n < 100) {
          let d = Math.floor(n / 10),
            u = n % 10;
          return decenas[d] + (u ? " y " + unidades[u] : "");
        }
        if (n < 1000) {
          let c = Math.floor(n / 100),
            r = n % 100;
          if (c === 1 && r === 0) return "cien";
          return centenas[c] + (r ? " " + seccion(r) : "");
        }
        if (n < 1000000) {
          let m = Math.floor(n / 1000),
            r = n % 1000;
          let miles = m === 1 ? "mil" : seccion(m) + " mil";
          return miles + (r ? " " + seccion(r) : "");
        }
        if (n < 1000000000000) {
          let mill = Math.floor(n / 1000000),
            r = n % 1000000;
          let millon = mill === 1 ? "un millón" : seccion(mill) + " millones";
          return millon + (r ? " " + seccion(r) : "");
        }
        return n;
      }
      let partes = num.toString().split(".");
      let entero = parseInt(partes[0]);
      let dec = partes[1] ? parseInt(partes[1].substring(0, 2)) : 0;
      let texto = seccion(entero).trim();
      if (dec > 0) texto += " con " + dec + "/100";
      return texto.charAt(0).toUpperCase() + texto.slice(1);
    },
  },
  async mounted() {
    const empresaService = new EmpresaService();
    try {
      const response = await empresaService.getFirstEmpresa();
      this.empresaLogo = response.data.logo;
      this.$emit("logo-ready");
    } catch (e) {
      this.empresaLogo = "";
      this.$emit("logo-ready");
    }
  },
};
</script>

<style scoped>
.totales-grid-wrapper {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 0;
}
.totales-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px 18px;
  max-width: 340px;
  margin-bottom: 8px;
  min-width: 240px;
  background: #f8fafc;
  border-radius: 8px;
  box-shadow: 0 1px 4px #0001;
  padding: 12px 18px 10px 18px;
}
.tot-label {
  font-weight: 500;
  color: #1976d2;
  text-align: right;
}
.tot-value {
  text-align: right;
  color: #333;
}
.tot-main {
  font-size: 1.15rem;
  font-weight: bold;
  color: #1976d2;
}
.proforma-pdf {
  padding: 32px 40px;
  font-family: "Segoe UI", Arial, sans-serif;
  color: #222;
  background: #fff;
  min-width: 600px;
  max-width: 800px;
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.logo {
  max-height: 60px;
  max-width: 180px;
  margin-right: 24px;
  width: auto;
  height: auto;
}
.header h1 {
  margin: 0;
  font-size: 2.2rem;
  color: #1976d2;
  letter-spacing: 2px;
}
.numero {
  font-size: 1.1rem;
  color: #555;
  margin-top: 4px;
}
.info {
  margin: 24px 0 16px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 32px;
  font-size: 1.05rem;
}
.detalle-section {
  margin: 24px 0 10px 0;
}
.detalle-section h3 {
  margin-bottom: 8px;
  color: #1976d2;
  font-size: 1.1rem;
}
.detalle-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.98rem;
  margin-bottom: 10px;
}
.detalle-table th,
.detalle-table td {
  border: 1px solid #e0e0e0;
  padding: 6px 10px;
  text-align: left;
}
.detalle-table th.text-center,
.detalle-table td.text-center {
  text-align: center;
}
.detalle-table th.text-right,
.detalle-table td.text-right {
  text-align: right;
}
.detalle-table th {
  background: #f4f8fb;
  color: #1976d2;
}
.detalle-table tr:nth-child(even) {
  background: #f9f9f9;
}
.total {
  margin: 18px 0 10px 0;
  font-size: 1.2rem;
  font-weight: bold;
  color: #1976d2;
}
.monto {
  font-size: 1.3rem;
  color: #388e3c;
}
.obs {
  margin-top: 18px;
  font-size: 1rem;
  color: #444;
  background: #f7f7f7;
  padding: 10px 14px;
  border-radius: 6px;
}
hr {
  border: none;
  border-top: 2px solid #1976d2;
  margin: 18px 0 12px 0;
}
</style>
